import React, { FunctionComponent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Link } from '../../atoms/link/Link'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { BodyTextSize } from '../../styles/typeStyles'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { mailTo, tID } from '../../utils/utils'

export type UserContactInformationCardProps = {
  userName: string
  dateOfBirth: string
  emailAddress: string
  careNavigatorEmail?: string
  careNavigatorPhone?: string
  phoneExtension?: string
}

const Container = styled.View<{ theme: ThemeType }>(
  ({
    theme: {
      spacing,
      breakpoints: { isMobileSized },
    },
  }) => ({
    marginBottom: isMobileSized ? spacing['32px'] : spacing['40px'],
  }),
)

const CardContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing, colors } }) => ({
  backgroundColor: colors.backgroundSection,
  borderRadius: spacing['8px'],
  padding: spacing['16px'],
  margin: `${spacing['8px']} 0px 0px`,
}))

const FieldContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginBottom: spacing['8px'],
}))

const ContactSection = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['4px'],
}))

export const UserContactInformationCard: FunctionComponent<UserContactInformationCardProps> = ({
  userName,
  dateOfBirth,
  emailAddress,
  careNavigatorEmail = 'care@lyrahealth.com',
  careNavigatorPhone = '(877) 505-7147',
  phoneExtension = '2',
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  const handleEmailPress = () => {
    mailTo(emailAddress)
  }

  const handleCareNavigatorEmailPress = () => {
    mailTo(careNavigatorEmail)
  }

  const handleCareNavigatorPhonePress = () => {
    const phoneNumber = careNavigatorPhone.replace(/[^0-9]/g, '')
    openUrl(`tel:${phoneNumber}`)
  }

  return (
    <Container testID={tID('UserContactInformationCard')}>
      <Title
        size={TitleSize.XSMALL}
        text={
          <FormattedMessage
            defaultMessage='Your contact information'
            description='Title for the user contact information card section'
          />
        }
        testID={tID('UserContactInformationCard-title')}
      />
      <CardContainer testID={tID('UserContactInformationCard-card')}>
        <FieldContainer>
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
            text={<FormattedMessage defaultMessage='Your name' description='Label for user name field' />}
            testID={tID('UserContactInformationCard-name-label')}
          />
          <BodyText size={BodyTextSize.DEFAULT} text={userName} testID={tID('UserContactInformationCard-name-value')} />
        </FieldContainer>
        <FieldContainer>
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
            text={<FormattedMessage defaultMessage='Date of birth' description='Label for date of birth field' />}
            testID={tID('UserContactInformationCard-dob-label')}
          />
          <BodyText
            size={BodyTextSize.DEFAULT}
            text={dateOfBirth}
            testID={tID('UserContactInformationCard-dob-value')}
          />
        </FieldContainer>
        <FieldContainer>
          <BodyText
            size={BodyTextSize.SMALL}
            color={colors.textSecondary}
            text={<FormattedMessage defaultMessage='Email address' description='Label for email address field' />}
            testID={tID('UserContactInformationCard-email-label')}
          />
          <Link
            text={emailAddress}
            onPress={handleEmailPress}
            size={BodyTextSize.DEFAULT}
            testID={tID('UserContactInformationCard-email-value')}
            accessibilityLabel={formatMessage(
              {
                defaultMessage: 'Email {emailAddress}',
                description: 'Accessibility label for email address link',
              },
              { emailAddress },
            )}
          />
        </FieldContainer>
        <ContactSection>
          <BodyText
            size={BodyTextSize.CAPTION}
            color={colors.textSecondary}
            text={
              <FormattedMessage
                defaultMessage='If you need to update your personal information, contact a care navigator at <emailLink>{email}</emailLink> or call <phoneLink>{phone}</phoneLink> ext {extension}'
                description='Footer text explaining how to update personal information'
                values={{
                  email: careNavigatorEmail,
                  phone: careNavigatorPhone,
                  extension: phoneExtension,
                  emailLink: (text: string) => (
                    <Link
                      text={text}
                      onPress={handleCareNavigatorEmailPress}
                      size={BodyTextSize.SMALL}
                      testID={tID('UserContactInformationCard-footer-email-link')}
                      accessibilityLabel={formatMessage(
                        {
                          defaultMessage: 'Email care navigator at {email}',
                          description: 'Accessibility label for care navigator email link',
                        },
                        { email: careNavigatorEmail },
                      )}
                    />
                  ),
                  phoneLink: (text: string) => (
                    <Link
                      text={text}
                      onPress={handleCareNavigatorPhonePress}
                      size={BodyTextSize.SMALL}
                      testID={tID('UserContactInformationCard-footer-phone-link')}
                      accessibilityLabel={formatMessage(
                        {
                          defaultMessage: 'Call care navigator at {phone}',
                          description: 'Accessibility label for care navigator phone link',
                        },
                        { phone: careNavigatorPhone },
                      )}
                    />
                  ),
                }}
              />
            }
            testID={tID('UserContactInformationCard-footer')}
          />
        </ContactSection>
      </CardContainer>
    </Container>
  )
}
